// Qué se le ofrece a una marca para contratar o cambiar su plan, y qué se muestra en
// el selector de marcas (ADR-2026-10-28-elegir-plan-de-marca). Puro: sin base ni React,
// para que dueño/miembro × sin plan/sin créditos se pruebe sin montar nada.
//
// Sólo existe para empresas con `company_profiles.facturacion_por_marca`: sin la bandera
// nadie llama a esto.

import { getPlanById } from '../../lib/checkoutPlans';
import { REBILL_PLAN_IDS } from '../../config';
import type { MotivoPricing } from '../pricing/pricingContexts';
import type { PlanView } from '../pricing/PricingCatalog';
import { lineaImagenes, lineaVideos } from '../pricing/pricingContent';
import type { MetadataDeMarca } from '../../lib/paymentServer';
import type { Saldo } from '../../lib/saldo';
import type { EstadoDelCheckoutRebill } from '../RebillCardPayment';

/**
 * Los planes que puede contratar una marca: por debajo son Ultra 1, 2 y 3 (mismos precios y
 * créditos del catálogo), pero en una marca se llaman por cuánto se usa
 * (ADR-2026-10-28-elegir-plan-de-marca-con-el-catalogo). El catálogo de la empresa, el
 * checkout y Configuración siguen diciendo "Ultra".
 */
export const PLANES_ULTRA_DE_MARCA = ['enterprise1', 'enterprise2', 'enterprise3'] as const;
type PlanUltraDeMarca = (typeof PLANES_ULTRA_DE_MARCA)[number];

/** Nombre y bajada de cada nivel en una marca. Precio y créditos salen del catálogo. */
export const NIVELES_DE_USO_DE_MARCA: Record<PlanUltraDeMarca, { nombre: string; bajada: string }> = {
  enterprise1: { nombre: 'Uso básico', bajada: 'Para una marca con un catálogo chico o que recién arranca' },
  enterprise2: { nombre: 'Uso medio', bajada: 'Para una marca que publica todas las semanas, con imágenes y videos' },
  enterprise3: { nombre: 'Uso intensivo', bajada: 'Para catálogos grandes y muchos videos todos los meses' },
};

/**
 * Planes de PRUEBA (ADR-2026-10-28-planes-de-prueba-de-marca): baratos y de renovación MENSUAL (así se
 * crearon en Rebill: `period: month`; se los había dado por diarios y no lo eran, ADR-2026-10-29-planes-de-prueba-mensuales),
 * sólo para empresas `dev_tester`. Espejo de `PLANES_DE_PRUEBA_DE_MARCA` del payment server,
 * que es quien los cobra y rechaza a cualquier otra empresa.
 */
export const PLANES_DE_PRUEBA_DE_MARCA = {
  marca_prueba_1: { nombre: 'Prueba 1', precio: 1000, creditos: 30, periodo: 'mes' },
  marca_prueba_2: { nombre: 'Prueba 2', precio: 2000, creditos: 60, periodo: 'mes' },
} as const;
type PlanDePrueba = keyof typeof PLANES_DE_PRUEBA_DE_MARCA;

/** En orden de nivel: los de prueba abajo de todo, para que "subir de nivel" sea entre ellos. */
export const PLANES_DE_MARCA = ['marca_prueba_1', 'marca_prueba_2', ...PLANES_ULTRA_DE_MARCA] as const;
export type PlanDeMarca = (typeof PLANES_DE_MARCA)[number];

export const esPlanDeMarca = (p: unknown): p is PlanDeMarca =>
  typeof p === 'string' && (PLANES_DE_MARCA as readonly string[]).includes(p);

const esPlanDePrueba = (p: string): p is PlanDePrueba => p in PLANES_DE_PRUEBA_DE_MARCA;

/** ¿Es uno de los planes de prueba (sólo `dev_tester`)? */
export const esPlanDePruebaDeMarca = (p: PlanDeMarca): boolean => esPlanDePrueba(p);

/** El nombre visible de un plan de marca: "Uso básico/medio/intensivo" o el de prueba. */
export function nombreDePlanDeMarca(plan: PlanDeMarca): string {
  return esPlanDePrueba(plan) ? PLANES_DE_PRUEBA_DE_MARCA[plan].nombre : NIVELES_DE_USO_DE_MARCA[plan].nombre;
}

export interface OpcionDePlanDeMarca {
  id: PlanDeMarca;
  nombre: string;
  /** ARS, del catálogo (`lib/checkoutPlans`): no se escribe a mano. */
  precio: number;
  /** Créditos por período. */
  creditos: number;
  /** Cada cuánto se cobra y se recargan los créditos: los de prueba, cada día. */
  periodo: 'dia' | 'mes';
}

/** "por día" / "por mes", para el precio y los créditos de una opción. */
export function periodoComoTexto(periodo: OpcionDePlanDeMarca['periodo']): string {
  return periodo === 'dia' ? 'por día' : 'por mes';
}

/**
 * Uso básico/medio/intensivo (Ultra 1/2/3) con precio y créditos del catálogo, en orden de
 * nivel. Con `devTester` (y el `pln_` de Rebill cargado) suma los de prueba abajo de todo.
 */
export function opcionesDePlanDeMarca(
  { devTester = false, planesDeRebill = REBILL_PLAN_IDS }: { devTester?: boolean; planesDeRebill?: Record<string, string> } = {},
): OpcionDePlanDeMarca[] {
  return PLANES_DE_MARCA.filter((id) => !esPlanDePrueba(id) || (devTester && !!planesDeRebill[id])).map((id) => {
    if (esPlanDePrueba(id)) return { id, ...PLANES_DE_PRUEBA_DE_MARCA[id] };
    const p = getPlanById(id);
    return { id, nombre: nombreDePlanDeMarca(id), precio: p?.price ?? 0, creditos: p?.max_fotos_mes ?? 0, periodo: 'mes' as const };
  });
}

/** El botón que cobra una opción: "Contratar Uso básico" / "Pasar a Uso medio". */
export function etiquetaDelBotonDePlanDeMarca(accion: 'suscribir' | 'cambiar', nombre: string): string {
  return `${accion === 'suscribir' ? 'Contratar' : 'Pasar a'} ${nombre}`;
}

/** "$109.999": el precio como lo muestra el plan de la marca. */
export const precioDePlanDeMarcaComoTexto = (n: number): string => `$${n.toLocaleString('es-AR')}`;

/**
 * Las opciones de Uso básico/medio/intensivo como cards del catálogo de planes
 * (`PlanCard` / `PlanCarousel`, ADR-2026-10-28-elegir-plan-de-marca-con-el-catalogo). Los
 * planes de prueba no van acá: se muestran aparte, en una fila chica.
 *
 * Sin features: los tres niveles traen lo mismo y se diferencian por créditos y precio.
 * "Se pueden generar" sale de los créditos con las mismas líneas que el catálogo de la empresa.
 */
export function cardsDePlanDeMarca(opciones: OpcionDePlanDeMarca[], accion: 'suscribir' | 'cambiar'): PlanView<PlanDeMarca>[] {
  return opciones
    .filter((o): o is OpcionDePlanDeMarca & { id: PlanUltraDeMarca } => !esPlanDePrueba(o.id))
    .map((o) => {
      const generates = [lineaImagenes(o.creditos), lineaVideos(o.creditos)];
      const cta = etiquetaDelBotonDePlanDeMarca(accion, o.nombre);
      return {
        key: o.id,
        content: {
          tab: o.nombre,
          title: o.nombre,
          subtitle: NIVELES_DE_USO_DE_MARCA[o.id].bajada,
          credits: o.creditos,
          generates,
          inherits: null,
          features: [],
          cta,
        },
        credits: o.creditos,
        generates,
        price: precioDePlanDeMarcaComoTexto(o.precio),
        period: periodoComoTexto(o.periodo),
        // Sin plan anual ni descuentos en las marcas.
        annual: { total: '', perMonth: '' },
        ivaIncluido: false,
        // El plan vigente nunca está entre las opciones (`vistaDelPlanDeMarca` lo saca).
        isCurrent: false,
        ctaLabel: cta,
        ctaDisabled: false,
      };
    });
}

export type ModoDelPlanDeMarca = 'contratar' | 'cambiar';

export type VistaDelPlanDeMarca =
  | { tipo: 'pedir_al_admin'; titulo: string; mensaje: string }
  | {
      tipo: 'elegir';
      /** Qué endpoint del payment server corresponde. */
      accion: 'suscribir' | 'cambiar';
      titulo: string;
      mensaje: string;
      opciones: OpcionDePlanDeMarca[];
      /** Ofrecer activar la recarga automática como salida (ADR-2026-10-29-sin-creditos-ofrece-la-recarga). */
      ofrecerRecarga: boolean;
    }
  | { tipo: 'tope'; titulo: string; mensaje: string; ofrecerRecarga: boolean };

export const MENSAJE_SIN_PLAN = 'Esta marca no tiene plan. Elegí uno para seguir.';

/**
 * Qué ve quien abre el plan de una marca. Decide el ESTADO de la billetera, no el `modo`
 * con que la abrieron: una marca sin plan siempre contrata (aunque la abran desde un
 * "mejorar plan") y una con plan siempre cambia de plan.
 *
 * Sin créditos con plan se ofrecen sólo los niveles de arriba: el de abajo no le
 * devuelve créditos hoy. Con créditos, cualquier otro nivel.
 */
export function vistaDelPlanDeMarca(e: {
  esDueno: boolean;
  plan: string | null;
  disponibles: number;
  modo: ModoDelPlanDeMarca;
  /** Empresa interna: ve también los planes de prueba. */
  devTester?: boolean;
  planesDeRebill?: Record<string, string>;
  /**
   * Se abrió porque lo que se pidió no alcanzó (el "sin créditos"), aunque quede algo de saldo:
   * con 2 créditos y un pedido de 3 la marca también está sin créditos para eso.
   */
  sinCreditos?: boolean;
  /**
   * La recarga automática está desactivada y, activándola, recargaría: plan al día, sin baja
   * agendada y sin cobro rechazado (`recargaActivable`). Con la recarga, sin créditos se ofrece.
   */
  recargaActivable?: boolean;
}): VistaDelPlanDeMarca {
  const tienePlan = esPlanDeMarca(e.plan);
  const sinCreditos = e.sinCreditos === true || e.disponibles <= 0;
  const conRecarga = tienePlan && sinCreditos && e.recargaActivable === true;
  // Una marca sin plan que chocó contra el saldo (gastó los de regalo): el título dice qué pasó. Sólo con
  // el aviso explícito: el alta de una marca nueva también la abre sin plan y con 0, y ahí no "se quedó".
  const chocoSinPlan = !tienePlan && e.sinCreditos === true;

  if (!e.esDueno) {
    return tienePlan
      ? {
          tipo: 'pedir_al_admin',
          titulo: sinCreditos ? 'Esta marca se quedó sin créditos' : 'El plan de esta marca',
          mensaje: !sinCreditos
            ? 'Pedile al admin de la cuenta que le cambie el plan.'
            : conRecarga
              ? 'Pedile al admin de la cuenta que le suba el plan o active la recarga automática.'
              : 'Pedile al admin de la cuenta que le suba el plan para seguir.',
        }
      : {
          tipo: 'pedir_al_admin',
          titulo: chocoSinPlan ? 'Esta marca se quedó sin créditos' : 'Esta marca no tiene plan',
          mensaje: 'Esta marca no tiene plan. Pedile al admin de la cuenta que elija uno para seguir.',
        };
  }

  const todas = opcionesDePlanDeMarca({ devTester: e.devTester, planesDeRebill: e.planesDeRebill });
  if (!tienePlan) {
    return {
      tipo: 'elegir',
      accion: 'suscribir',
      titulo: chocoSinPlan ? 'Esta marca se quedó sin créditos' : 'Elegí un plan para esta marca',
      mensaje: MENSAJE_SIN_PLAN,
      opciones: todas,
      ofrecerRecarga: false,
    };
  }

  const nivel = PLANES_DE_MARCA.indexOf(e.plan as PlanDeMarca);
  const opciones = todas.filter((o) => {
    const i = PLANES_DE_MARCA.indexOf(o.id);
    return sinCreditos ? i > nivel : i !== nivel;
  });
  if (opciones.length === 0) {
    return {
      tipo: 'tope',
      titulo: 'Esta marca se quedó sin créditos',
      mensaje: conRecarga
        ? 'Ya tiene el plan más alto. Activá la recarga automática para seguir, o esperá a que se renueven los créditos.'
        : 'Ya tiene el plan más alto. Los créditos vuelven con la próxima renovación.',
      ofrecerRecarga: conRecarga,
    };
  }
  return {
    tipo: 'elegir',
    accion: 'cambiar',
    titulo: sinCreditos ? 'Esta marca se quedó sin créditos' : 'Cambiá el plan de esta marca',
    mensaje: !sinCreditos
      ? 'Elegí el plan nuevo. Se cobra hoy con la tarjeta de la cuenta.'
      : conRecarga
        ? 'Pasala a un plan más alto o activá la recarga automática para seguir. Se cobra con la tarjeta de la cuenta.'
        : 'Pasala a un plan más alto para seguir. Se cobra hoy con la tarjeta de la cuenta.',
    opciones,
    ofrecerRecarga: conRecarga,
  };
}

/**
 * ¿Activar la recarga automática haría que la marca recargue? Lo mismo que mira el servidor de pagos
 * (`necesitaRecarga`), salvo el saldo: plan al día (`active`), sin baja agendada y sin cobro rechazado.
 * Si ya está activada, no hay nada que ofrecer.
 */
export function recargaActivable(
  b: { plan: string | null; status: string | null; fechaCancelacionProgramada: string | null; cobroRechazadoAt: string | null } | undefined,
  recargaActiva: boolean,
): boolean {
  if (!b || recargaActiva) return false;
  return esPlanDeMarca(b.plan) && b.status === 'active' && !b.fechaCancelacionProgramada && !b.cobroRechazadoAt;
}

const creditosComoTexto = (n: number): string => {
  const c = Math.max(0, Math.floor(n));
  return `${c.toLocaleString('es-AR')} ${c === 1 ? 'crédito' : 'créditos'}`;
};

/** La etiqueta del plan de la marca activa junto al saldo (sidebar). */
export function etiquetaDelPlanDeMarca(plan: string | null): string {
  return esPlanDeMarca(plan) ? nombreDePlanDeMarca(plan) : 'Marca sin plan';
}

/** La línea de plan y saldo de una marca en el selector. Sin billetera = sin plan. */
export function lineaDePlanDeMarca(
  b: { plan: string | null; disponibles: number } | undefined,
): { texto: string; sinPlan: boolean } {
  if (!b) return { texto: 'Sin plan', sinPlan: true };
  if (!esPlanDeMarca(b.plan)) return { texto: `Sin plan · ${creditosComoTexto(b.disponibles)}`, sinPlan: true };
  const nombre = nombreDePlanDeMarca(b.plan);
  return { texto: `${nombre} · ${creditosComoTexto(b.disponibles)}`, sinPlan: false };
}

/**
 * El plan de Atlas que espera `RebillCardPayment` (`planId`) a partir del `checkout` que
 * devuelve el payment server con `needs_checkout`: ese trae el `pln_` de Rebill en
 * `plan_id` y el plan de Atlas en `metadata.plan`. Se prefiere la metadata; si no viene,
 * se busca el `pln_` en el mapa del front (`REBILL_PLAN_IDS`).
 *
 * El `pln_` del server (`checkout.plan_id`) NO se le pasa al formulario: `RebillCardPayment`
 * sólo recibe el plan de Atlas y lo traduce con `REBILL_PLAN_IDS` (no tiene prop para un `pln_`
 * crudo). Los dos mapas son copias (`REBILL_PLAN_BY_ID` del payment server); si algún día
 * divergen, sumar esa prop y preferir el del server.
 */
export function planDelCheckout(
  checkout: { plan_id: string; metadata: MetadataDeMarca },
  planesDeRebill: Record<string, string>,
): PlanDeMarca | null {
  if (esPlanDeMarca(checkout.metadata?.plan)) return checkout.metadata.plan;
  const id = PLANES_DE_MARCA.find((p) => planesDeRebill[p] && planesDeRebill[p] === checkout.plan_id);
  return id ?? null;
}

/** La metadata de marca como la manda el `<rebill-checkout>` (todo texto). */
export function metadataParaElCheckout(m: MetadataDeMarca): Record<string, string> {
  return {
    id_company: String(m.id_company),
    brand_id: String(m.brand_id),
    user_id: String(m.user_id),
    plan: String(m.plan),
    tipo: 'marca',
  };
}

/**
 * ¿Este motivo del catálogo es de saldo / mejorar plan? Esos, con facturación por marca,
 * van al plan de la marca activa. `bloqueado` es una función que el plan de la EMPRESA no
 * incluye (multi-marca, modos con candado) y sigue yendo al catálogo.
 */
export function motivoVaAlPlanDeMarca(motivo: MotivoPricing): boolean {
  return motivo === 'sin' || motivo === 'pocos' || motivo === 'mejorar';
}

const ERRORES: Record<string, string> = {
  no_es_el_dueno: 'Sólo el admin de la cuenta puede cambiar el plan de una marca.',
  sin_bandera: 'Esta cuenta no tiene planes por marca. Escribinos y lo revisamos.',
  marca_ajena: 'Esta marca no es de tu cuenta. Actualizá la página y probá de nuevo.',
  plan_no_permitido: 'Las marcas sólo pueden contratar Uso básico, Uso medio o Uso intensivo.',
  sin_plan: 'Esta marca todavía no tiene plan. Elegí uno para contratarlo.',
  ya_tiene_plan: 'Esta marca ya tiene ese plan.',
  // La billetera quedó en `baja_pendiente_en_rebill`: la sub vieja sigue viva en Rebill y el cron
  // reintenta cancelarla. Una sub nueva cobraría dos veces (ADR-2026-10-28-baja-pendiente-frena-el-alta).
  baja_pendiente: 'Estamos terminando de dar de baja el plan anterior de esta marca. Probá en unos minutos o escribinos.',
  duplicate_request: 'Ya estamos procesando este pago. Esperá unos segundos.',
  payment_rejected: 'La tarjeta rechazó el pago. Probá con otra o revisala con tu banco.',
  idempotency_mismatch: 'Hubo un cambio en el pedido. Cerrá y probá de nuevo.',
  gateway_error: 'El procesador de pagos no respondió. No se te cobró nada; probá en unos minutos.',
  // Sin sesión (o vencida): el pedido ni sale, o el payment server lo rechaza con 401.
  unauthorized: 'No pudimos validar tu sesión. Cerrá sesión, volvé a entrar y probá de nuevo.',
};

/** El texto para el usuario de un rechazo tipado del payment server. */
export function mensajeDeErrorDePlanDeMarca(error: string): string {
  return ERRORES[error] ?? 'No pudimos completar el cambio de plan. Probá de nuevo en unos minutos.';
}

/** Qué plan de marca abrir y en qué modo. `brandId` null = con bandera pero sin marca resuelta. */
export interface PedidoDePlanDeMarca {
  brandId: string | null;
  modo: ModoDelPlanDeMarca;
}

/**
 * ¿Un "sin créditos" o "mejorar plan" va al plan de la marca activa? Con la bandera SIEMPRE
 * (aunque no haya marca resuelta: el modal muestra su error, nunca se cae al catálogo de la
 * empresa — ADR-2026-10-28-elegir-plan-de-marca-2). `null` = sigue con lo de siempre: sin
 * bandera, o sin saldo leído (el caller lo lee antes con `asegurarSaldoLeido`).
 */
export function pedidoDePlanDeMarca(saldo: Saldo | null): PedidoDePlanDeMarca | null {
  if (!saldo?.facturaPorMarca) return null;
  return { brandId: saldo.brandId, modo: saldo.sinPlanDeMarca || !saldo.brandId ? 'contratar' : 'cambiar' };
}

/**
 * ¿Se puede vender un plan de EMPRESA (el catálogo de `/subscription`, `/checkout`, el alta por
 * link)? Con facturación por marca no: la empresa no tiene suscripción y el payment server
 * rechaza el cobro (ADR-2026-10-28-checkout-de-empresa-cerrado-con-facturacion-por-marca).
 * Sin saber la bandera, tampoco: falla cerrado (ADR-2026-10-29-paywall-con-planes-por-marca).
 */
export function ventaDePlanDeEmpresa(facturaPorMarca: boolean | null | undefined): 'vender' | 'no_vender' | 'sin_saber' {
  if (facturaPorMarca === false) return 'vender';
  if (facturaPorMarca === true) return 'no_vender';
  return 'sin_saber';
}

export type VistaDelPlanDeEmpresa =
  | { tipo: 'leyendo' }
  | { tipo: 'catalogo' }
  | { tipo: 'plan_de_marca'; pedido: PedidoDePlanDeMarca }
  | { tipo: 'sin_saber' };

/**
 * Qué muestran `/subscription` y `/checkout` (ADR-2026-10-29-paywall-con-planes-por-marca).
 * `vigente` = el saldo de la marca activa (el último vigente o el de la lectura que se esperó);
 * `bandera` = la de cualquier lectura, aunque sea de otra marca (la bandera es de la empresa):
 *  - con el saldo de la marca activa decide `pedidoDePlanDeMarca`, igual que el "sin créditos":
 *    con bandera, el plan de la marca (también sin marca resuelta: el modal muestra su error);
 *    sin bandera, el catálogo de siempre.
 *  - sin ese saldo, una bandera apagada alcanza para el catálogo; con bandera hace falta la marca
 *    activa, y sin bandera sabida no se muestra el catálogo: `leyendo` mientras se lee, y
 *    `sin_saber` (reintentar o volver) si la lectura falló o tardó de más.
 */
export function vistaDelPlanDeEmpresa(e: { vigente: Saldo | null; bandera: boolean | null; leyendo: boolean }): VistaDelPlanDeEmpresa {
  if (e.vigente) {
    const pedido = pedidoDePlanDeMarca(e.vigente);
    return pedido ? { tipo: 'plan_de_marca', pedido } : { tipo: 'catalogo' };
  }
  if (ventaDePlanDeEmpresa(e.bandera) === 'vender') return { tipo: 'catalogo' };
  return e.leyendo ? { tipo: 'leyendo' } : { tipo: 'sin_saber' };
}

/** Clave en `localStorage` de la idempotencia de un cobro de marca: por (usuario, marca, acción, plan). */
export function claveDeCobroDeMarca(userId: string, brandId: string, accion: 'suscribir' | 'cambiar', plan: string): string {
  return `atlas:rebill:marca:${userId}:${brandId}:${accion}:${plan}`;
}

export type PasoTrasCobro =
  | { tipo: 'confirmar'; borrarClave: true }
  | { tipo: 'verificar'; borrarClave: false }
  | { tipo: 'checkout'; plan: PlanDeMarca; metadata: Record<string, string>; borrarClave: true }
  | { tipo: 'reintentar_como_alta'; borrarClave: true }
  | { tipo: 'error'; mensaje: string; borrarClave: boolean };

/**
 * Qué hacer con la respuesta de `/marca/suscribir` o `/marca/cambiar-plan`.
 *
 * `'ambiguo'` = 5xx o corte de red: PUDO haber cobro, y el payment server no guarda ese
 * resultado ni arma su cooldown, así que reintentar antes de que llegue el webhook podría
 * cobrar dos veces. Se verifica la billetera y NO se ofrece pagar de nuevo; la clave se
 * conserva (un reintento tras reabrir cae en la misma operación).
 */
export function siguientePasoTrasCobro(
  r: { ok: true; subscription_id?: string } | { ok: false; error: string; checkout?: { plan_id: string; metadata: MetadataDeMarca } } | 'ambiguo',
  planesDeRebill: Record<string, string>,
  { accion = 'suscribir' }: { accion?: 'suscribir' | 'cambiar' } = {},
): PasoTrasCobro {
  if (r === 'ambiguo') return { tipo: 'verificar', borrarClave: false };
  if (r.ok) return { tipo: 'confirmar', borrarClave: true };
  if (r.error === 'sin_plan' && accion === 'cambiar') return { tipo: 'reintentar_como_alta', borrarClave: true };
  if (r.error === 'needs_checkout' && r.checkout) {
    const plan = planDelCheckout(r.checkout, planesDeRebill);
    if (plan) return { tipo: 'checkout', plan, metadata: metadataParaElCheckout(r.checkout.metadata), borrarClave: true };
  }
  return { tipo: 'error', mensaje: mensajeDeErrorDePlanDeMarca(r.error), borrarClave: r.error !== 'duplicate_request' };
}

/** El copy de la barra de créditos con facturación por marca: habla de "esta marca". */
export function copyDeLaBarraDeMarca(e: {
  estado: 'sin' | 'pocos';
  restantes: number | undefined;
  sinPlan: boolean;
  esMiembro: boolean;
}): { gancho: string; remate: string; boton: string } {
  if (e.sinPlan && e.estado === 'sin') {
    return e.esMiembro
      ? { gancho: 'Esta marca no tiene plan.', remate: 'Pedile al admin de la cuenta que elija uno.', boton: 'Ver el plan' }
      : { gancho: 'Esta marca no tiene plan.', remate: 'Elegí uno para seguir.', boton: 'Elegir plan' };
  }
  const n = Math.max(0, Math.floor(e.restantes ?? 0));
  const gancho =
    e.estado === 'sin'
      ? 'Esta marca se quedó sin créditos.'
      : n === 1
        ? 'A esta marca le queda 1 crédito.'
        : `A esta marca le quedan ${n.toLocaleString('es-AR')} créditos.`;
  if (e.esMiembro) return { gancho, remate: 'Pedile al admin de la cuenta que le suba el plan.', boton: 'Ver el plan' };
  return e.sinPlan
    ? { gancho, remate: 'Elegí un plan para esta marca.', boton: 'Elegir plan' }
    : { gancho, remate: 'Subile el plan para seguir.', boton: 'Cambiar plan' };
}

/**
 * Tras esperar el plan en la billetera: ¿se borra la clave de idempotencia? Sólo cuando un
 * cobro ambiguo terminó verificado (el ok directo ya la borró al recibir la respuesta; uno
 * ambiguo sin verificar la conserva para que un reintento caiga en la misma operación).
 * ADR-2026-10-28-elegir-plan-de-marca-3.
 */
export function borrarClaveTrasEsperar(e: { verificando: boolean; confirmado: boolean }): boolean {
  return e.verificando && e.confirmado;
}

/**
 * Cuánto dura como máximo el candado de "Rebill está procesando la tarjeta". `tarjeta_enviada`
 * sale en cada `submit` del form de Rebill, incluso los que el SDK frena antes de mandar
 * (ADR-2026-10-16-pago-directo-espera-y-medicion): sin tope, el modal quedaría sin poder
 * cerrarse para siempre.
 */
export const TOPE_TARJETA_ENVIADA_MS = 90_000;

/**
 * ¿Hay un submit de tarjeta que pudo haber cobrado? Se prende con `tarjeta_enviada` y sólo lo
 * apaga un rechazo de Rebill (definitivo: no cobró). A diferencia del candado de cierre (que se
 * suelta a los `TOPE_TARJETA_ENVIADA_MS`), esto dura toda la apertura del modal.
 */
export function tarjetaEnCamino(previo: boolean, estado: EstadoDelCheckoutRebill): boolean {
  if (estado === 'tarjeta_enviada') return true;
  if (estado === 'rechazo') return false;
  return previo;
}

/**
 * Qué es cerrar el modal: `cerrar` = sin cambios (`onCerrar`, que en el alta de una marca nueva
 * descarta su borrador) o `listo` (`onListo`: pudo haber un cobro). Es `listo` después de un
 * cobro y también con una tarjeta enviada sin respuesta, aunque el candado ya se haya soltado:
 * descartar la marca ahí dejaría un cobro sin marca (ADR-2026-10-28-marca-nueva-con-pago-2).
 */
export function alCerrarElPlan(e: { fase: string; tarjetaEnviada: boolean }): 'listo' | 'cerrar' {
  if (e.fase === 'listo' || e.fase === 'sin_confirmar' || e.tarjetaEnviada) return 'listo';
  return 'cerrar';
}

/**
 * Qué hace el botón de abajo del checkout. Con una tarjeta enviada sin respuesta (el candado
 * ya se soltó al tope) el cobro pudo haber salido: volver a los planes ofrecería pagar de
 * nuevo (y `needs_checkout` ya borró la clave de idempotencia; el checkout embebido no la
 * usa), así que se verifica la billetera como un cobro ambiguo
 * (ADR-2026-10-28-review-greptile-plan-por-marca).
 */
export function alSalirDelCheckout(e: { tarjetaEnviada: boolean }): 'planes' | 'verificar' {
  return e.tarjetaEnviada ? 'verificar' : 'planes';
}
